import { ProductType } from '@tink/core/src/types';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import _sortBy from 'lodash/sortBy';
import { captureMessage } from '@tink/core/src/sentry';
import fuzzysort from 'fuzzysort';
import { MarketCode } from '@tink/config/src/markets';
import { getRefreshableItemsFromScopes } from '../authorize/urlParams';
import { Provider, ProviderEntity, ProviderField } from '../models/provider';
import { RefreshableItem } from '../models/refreshableItem';
import { CAPABILITY_MAPPER } from '../models/refreshableItemMapper';
import { BankIdAuthenticationType } from '../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/organisms/BankIdAuthWithSupplementalContainer/BankIdAuthWithSupplementalContainer';
import { DemoBankScenario } from '../models/demobankScenarios';
import { fold } from './string-utils';
import {
  ProviderSortingStrategy,
  ProviderViewSet,
  ProviderViewSetGroup,
} from './provider-view-set';

export type SearchKeys = (keyof ProviderEntity)[];

type GetFuzzySortOptionsParams = {
  searchKeys: SearchKeys;
};

type SearchProvidersParams = {
  providers: ProviderEntity[];
  searchKeys: SearchKeys;
  searchString: string;
  version: string;
};

const getFuzzySortOptions = ({
  searchKeys,
}: GetFuzzySortOptionsParams): Fuzzysort.KeysOptions<ProviderEntity> => ({
  keys: searchKeys,
  limit: 100,
});

export const searchProviders = ({
  providers,
  searchKeys,
  searchString,
  version,
}: SearchProvidersParams) => {
  const fuzzySortOptions = getFuzzySortOptions({ searchKeys });
  const searchResult = fuzzysort.go(searchString, providers, fuzzySortOptions);
  const searchResultProviders = searchResult.map(({ obj }) => obj);

  const providerViewSet = new ProviderViewSet(searchResultProviders, version, undefined, undefined);

  const shouldSortAlphabetically = false;

  return providerViewSet.groupByDisplayName(shouldSortAlphabetically);
};

export const findProviderByName = (
  providers: ProviderEntity[],
  name?: string
): ProviderEntity | undefined => {
  return providers.find((p) => p.name === name);
};

export const isProviderMatchedByQuery = (provider: ProviderEntity, queries: string[]) => {
  const providerDisplayName = provider.displayName.toLowerCase();
  const providerGroupDisplayName = (provider.groupDisplayName || '').toLowerCase();

  const matchingDisplayName = queries.map((str) => providerDisplayName.includes(str));
  const matchingGroupDisplayName = queries.map((str) => providerGroupDisplayName.includes(str));

  if (
    matchingDisplayName.some((v) => v === false) &&
    matchingGroupDisplayName.some((v) => v === false)
  ) {
    return false;
  }

  return true;
};

export const searchGroupProviders = (
  setGroups: ProviderViewSetGroup[],
  searchString: string
): ProviderViewSetGroup[] => {
  const queries = searchString.toLowerCase().split(' ').filter(Boolean);

  return setGroups.filter((setGroup) =>
    setGroup.providers.some((provider) => isProviderMatchedByQuery(provider, queries))
  );
};

export const findProviderViewSetGroupByTitle = (groups: ProviderViewSetGroup[], title: string) => {
  return groups.find((group) => {
    return fold(group.title).toLowerCase() === fold(title).toLowerCase();
  });
};

export type GetProviderRefreshableItemsPayload = {
  scope: string;
  provider: ProviderEntity;
  productType: ProductType;
  refreshableItems: RefreshableItem[];
};

/**
 *
 * @deprecated
 */
export const getProviderRefreshableItems = ({
  scope,
  provider,
  productType,
  refreshableItems,
}: GetProviderRefreshableItemsPayload): RefreshableItem[] => {
  if (productType === ProductType.PAY) {
    return [];
  }

  if (productType === ProductType.ACCOUNT_CHECK) {
    return [
      RefreshableItem.CHECKING_ACCOUNTS,
      RefreshableItem.SAVING_ACCOUNTS,
      RefreshableItem.IDENTITY_DATA,
    ];
  }

  if (productType === ProductType.INCOME_CHECK || productType === ProductType.EXPENSE_CHECK) {
    return [
      RefreshableItem.CHECKING_ACCOUNTS,
      RefreshableItem.SAVING_ACCOUNTS,
      RefreshableItem.CREDITCARD_ACCOUNTS,
      RefreshableItem.LOAN_ACCOUNTS,
      RefreshableItem.INVESTMENT_ACCOUNTS,
      RefreshableItem.CHECKING_TRANSACTIONS,
      RefreshableItem.SAVING_TRANSACTIONS,
      RefreshableItem.CREDITCARD_TRANSACTIONS,
      RefreshableItem.LOAN_TRANSACTIONS,
      RefreshableItem.INVESTMENT_TRANSACTIONS,
      RefreshableItem.IDENTITY_DATA,
      RefreshableItem.EINVOICES,
      RefreshableItem.TRANSFER_DESTINATIONS,
    ];
  }

  if (productType === ProductType.TRANSACTIONS || productType === ProductType.PRODUCTS) {
    return refreshableItems;
  }

  const requestedRefreshableItems = getRefreshableItemsFromScopes(scope);

  // This is a list of capabilities that we know the provider supports
  const providerCapabilities = new Set(provider.capabilities);

  // This makes sure that at least one of the provider capability that maps to that refreshable item is supported by the provider,
  // otherwise an error would be raised because of provider capability validation on the backend
  return requestedRefreshableItems.filter((refreshableItem) =>
    CAPABILITY_MAPPER[refreshableItem].some((providerCapability) =>
      providerCapabilities.has(providerCapability)
    )
  );
};

export const getBankIdAuthenticationType = (providerName: string) =>
  userAgent.isDesktop && Provider.isRequiringAnimatedQRCode(providerName)
    ? BankIdAuthenticationType.BANK_ID_ANIMATED_QR_CODE
    : BankIdAuthenticationType.BANK_ID_AUTO_START_TOKEN;

type ProviderKeyBeingStringOrUndefined = keyof {
  [Property in keyof ProviderEntity as ProviderEntity[Property] extends string | undefined
    ? Property
    : never]: unknown;
};

type SortProvidersParams = {
  providers: ProviderEntity[];
  sortingKey: ProviderKeyBeingStringOrUndefined;
};

export const sortProvidersAlphabetically = ({ providers, sortingKey }: SortProvidersParams) => {
  const hasValidSortingValues = providers.every(
    (provider) => typeof provider[sortingKey] === 'string' && provider[sortingKey] !== ''
  );

  if (!hasValidSortingValues) {
    return providers;
  }

  const alphabeticallySorted = _sortBy(providers, (provider) => [
    (provider[sortingKey] as string).toUpperCase(),
    provider[sortingKey],
  ]);

  return alphabeticallySorted;
};

export const findProvider = (
  providers: ProviderEntity[],
  providerName: string,
  version: string
): [string, ProviderEntity?] => {
  let errorMessage = '';
  const provider = findProviderByName(providers, providerName);

  if (provider === undefined) {
    errorMessage = `Provider '${providerName}' does not exist`;

    captureMessage('Provider does not exist', {
      custom: {
        provider: providerName,
      },
    });
  } else if (Provider.isBlocklisted(provider, version)) {
    errorMessage = `Provider '${providerName}' is blacklisted`;
  } else if (!Provider.isEnabled(provider) && !Provider.isTemporaryDisabled(provider)) {
    errorMessage = `Provider '${providerName}' is not enabled`;
  }

  return [errorMessage, provider];
};

export const addDemobankCredentialsToProvider = (
  provider: ProviderEntity,
  scenario: DemoBankScenario
): ProviderEntity => {
  const demobankPassword = scenario.password;
  const demobankUsername = scenario.username;

  const fields: ProviderField[] = provider.fields.map((f) => {
    if (f.name === 'username' && demobankUsername) {
      return { ...f, value: demobankUsername, valid: true };
    }

    if (f.name === 'password' && demobankPassword) {
      return { ...f, value: demobankPassword, valid: true };
    }

    return f;
  });

  return { ...provider, fields };
};

type LimitProviderArgs = {
  providerGroups: ProviderViewSetGroup[];
  market: MarketCode;
  maximum?: number;
  isSearching: boolean;
  providerSortingStrategy: ProviderSortingStrategy;
};

/**
 * Method that takes a "maximum" and returns that maximum number of providers
 * to limit the providers returned in markets with a large number.
 * @param providerGroups
 * @param market
 * @param maximum
 * @param isSearching
 * @param providerSortingStrategy
 */
export const limitNumberOfProviders = ({
  providerGroups,
  market,
  maximum = 100,
  isSearching = false,
  providerSortingStrategy = ProviderSortingStrategy.BY_POPULAR_FLAG,
}: LimitProviderArgs): ProviderViewSetGroup[] => {
  if (market === MarketCode.US) {
    if (isSearching) {
      /* If we are in a search, we want to return maximum number of results */
      return providerGroups.slice(0, maximum);
    }

    /*
     * If we are not in a search but strategy is rank,
     * we want to only return banks with rank > 0.
     */
    if (providerSortingStrategy === ProviderSortingStrategy.BY_RANK_PROPERTY) {
      const providersWithNonZeroRank = providerGroups.filter((p) => p.rank !== 0);

      /*
       * This is a safeguard in case we don't have any non-zero ranked providers,
       * we return all of them because that is probably better than returning none at all.
       */
      if (providersWithNonZeroRank.length > 0) {
        return providersWithNonZeroRank;
      }
    }
  }

  return providerGroups;
};

export const getProviderSortingStrategy = (market: MarketCode): ProviderSortingStrategy => {
  if (market === MarketCode.US) {
    return ProviderSortingStrategy.BY_RANK_PROPERTY;
  }

  return ProviderSortingStrategy.BY_POPULAR_FLAG;
};
